import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { UserProfile, ChatAccessAction } from '@/core/BusinessRulesEngine/types';
import { debugLogger } from '@/utils/debugLogger';

export type ChatAccessContext = 'list' | 'conversation';

export interface UseChatAccessOptions {
  context: ChatAccessContext;
  targetUsername?: string; // obrigatório se context === 'conversation'
}

export interface ChatAccessResult {
  allowed: boolean;
  isLoading: boolean;
  context: ChatAccessContext;
  missingRequirements: string[];
  actions?: ChatAccessAction[];
  nextActionUrl?: string;
  nextActionLabel?: string;
  reason?: string;
  targetProfile?: { id: string; username?: string } | null;
  isUserBlocked?: boolean;
}

export const useChatAccess = (options: UseChatAccessOptions): ChatAccessResult => {
  const { context, targetUsername } = options;
  const { profileData, loading: authLoading } = useAuth();
  const [isLoadingTarget, setIsLoadingTarget] = useState(false);
  const [result, setResult] = useState<ChatAccessResult>({
    allowed: false,
    isLoading: true,
    context,
    missingRequirements: [],
  });

  useEffect(() => {
    const checkAccess = async () => {
      debugLogger.debug('chat-access:start', { context, targetUsername });

      // 1. Loading state
      if (authLoading) {
        debugLogger.debug('chat-access:loading');
        setResult({
          allowed: false,
          isLoading: true,
          context,
          missingRequirements: [],
        });
        return;
      }

      // 2. Profile not found - soft block
      if (!profileData) {
        debugLogger.warn('chat-access:no-profile');
        setResult({
          allowed: false,
          isLoading: false,
          context,
          missingRequirements: ['Carregue seu perfil para continuar'],
          nextActionUrl: '/main/profile',
          nextActionLabel: 'Ir para Perfil',
        });
        return;
      }

      // 3. Usar novo método canAccessChat do BRE
      const chatAccessResult = businessRulesEngine.canAccessChat(profileData);
      
      if (!chatAccessResult.allowed) {
        debugLogger.debug('chat-access:blocked', { 
          reason: chatAccessResult.reason, 
          actions: chatAccessResult.data?.actions 
        });
        
        const actions: ChatAccessAction[] = chatAccessResult.data?.actions || [];
        const primaryAction = actions.find(a => a.highlight) || actions.find(a => a.primary) || actions[0];
        
        setResult({
          allowed: false,
          isLoading: false,
          context,
          missingRequirements: chatAccessResult.data?.missingSections || [chatAccessResult.reason || 'Acesso bloqueado'],
          actions,
          nextActionUrl: primaryAction?.url,
          nextActionLabel: primaryAction?.label,
          reason: chatAccessResult.reason,
        });
        return;
      }

      // 4. Se context === 'list', permitir acesso (já pode ver os chats)
      if (context === 'list') {
        debugLogger.debug('chat-access:list:allowed');
        setResult({
          allowed: true,
          isLoading: false,
          context,
          missingRequirements: [],
        });
        return;
      }

      // 5. Se context === 'conversation', validar target user
      if (context === 'conversation') {
        if (!targetUsername) {
          debugLogger.error('chat-access:conversation:no-target-username');
          setResult({
            allowed: false,
            isLoading: false,
            context,
            missingRequirements: ['Usuário não especificado'],
            nextActionUrl: '/main/chats',
            nextActionLabel: 'Voltar aos Chats',
          });
          return;
        }

        setIsLoadingTarget(true);
        
        try {
          // Buscar target profile
          const { data: targetData, error: targetError } = await supabase
            .from('profiles_public')
            .select('id, username, role, hidden, plan_type, verification_level')
            .eq('username', targetUsername)
            .maybeSingle();

          if (targetError) {
            debugLogger.error('chat-access:conversation:target-fetch-error', { error: targetError });
            setResult({
              allowed: false,
              isLoading: false,
              context,
              missingRequirements: ['Erro ao carregar perfil do usuário'],
              nextActionUrl: '/main/chats',
              nextActionLabel: 'Voltar aos Chats',
            });
            return;
          }

          if (!targetData) {
            debugLogger.warn('chat-access:conversation:target-not-found', { targetUsername });
            setResult({
              allowed: false,
              isLoading: false,
              context,
              missingRequirements: ['Usuário não encontrado'],
              nextActionUrl: '/main/chats',
              nextActionLabel: 'Voltar aos Chats',
            });
            return;
          }

          // Mapear para UserProfile do BRE
          const sourceProfile: UserProfile = {
            id: profileData.id,
            role: profileData.role,
            plan_type: profileData.plan_type,
            hidden: profileData.hidden,
            verification_level: profileData.verification_level,
          };

          const targetProfile: UserProfile = {
            id: targetData.id,
            role: targetData.role,
            plan_type: targetData.plan_type,
            hidden: targetData.hidden,
            verification_level: targetData.verification_level,
          };

          // Validar interação via BRE
          const breResult = await businessRulesEngine.canInteractWith(sourceProfile, targetProfile);

          if (!breResult.allowed) {
            const isBlocked = breResult.metadata?.blocked === true;
            debugLogger.debug('chat-access:conversation:bre-denied', { reason: breResult.reason, isBlocked });
            setResult({
              allowed: false,
              isLoading: false,
              context,
              missingRequirements: isBlocked ? [] : [breResult.reason || 'Interação não permitida'],
              reason: breResult.reason,
              isUserBlocked: isBlocked,
              nextActionUrl: isBlocked ? '/main/chats' : `/main/profile/${targetUsername}`,
              nextActionLabel: isBlocked ? 'Voltar aos Chats' : `Ver perfil de ${targetUsername}`,
              targetProfile: { id: targetData.id, username: targetData.username },
            });
            return;
          }

          // Tudo OK!
          debugLogger.debug('chat-access:conversation:allowed', { targetUsername });
          setResult({
            allowed: true,
            isLoading: false,
            context,
            missingRequirements: [],
            targetProfile: { id: targetData.id, username: targetData.username },
          });
        } catch (error) {
          debugLogger.error('chat-access:conversation:unexpected-error', { error });
          setResult({
            allowed: false,
            isLoading: false,
            context,
            missingRequirements: ['Erro ao validar acesso'],
            nextActionUrl: '/main/chats',
            nextActionLabel: 'Voltar aos Chats',
          });
        } finally {
          setIsLoadingTarget(false);
        }
      }
    };

    checkAccess();
  }, [context, targetUsername, profileData, authLoading]);

  return {
    ...result,
    isLoading: result.isLoading || isLoadingTarget,
  };
};
